import { useRef, useState } from 'react';
import { handleGetBusinessConfig, handleGetEvaluationStatus } from '@/api/grt/evaluation';
import { getBusinessSearchParams } from '@/utils/Page';
import ToastUtils from '@/utils/ToastUtils';

// 评价场景语义 key（对应 evaluation.config.json 的 scenes key）
export const SCENE_KEY = {
    caseSimulation: 'caseSimulation' // 案例仿真（scene=3）
};
// 案例仿真评价场景码（查询评价状态接口入参用）
export const CASE_SIMULATION_SCENE = 3;
// 开放题固定约定码：提交报文中映射到后端 suggestion 字段（非 dimension_scores）
export const SUGGESTION_CODE = 'SUGGESTION';

/**
 * 案例仿真评价功能公共 hook（PC / H5 复盘报告页共用）
 * 职责：评价开关查询、本人已评价状态查询、弹窗开关与点击/提交回调
 * @param {string} agentId 智能体 id（business_id 兜底值 / resource_id）
 * @param {string} caseId  本次仿真 runtime_session_id
 * @param {string} sceneId 场景 id
 */
const useEvaluation = ({ agentId, caseId, sceneId }) => {
    const [evaluateOpen, setEvaluateOpen] = useState(0); // evaluation_agent_open 评价开关
    const [evaluated, setEvaluated] = useState(false); // evl_flag 是否已评价
    const [evaluateVisible, setEvaluateVisible] = useState(false); // 评价弹窗显隐
    const evalInitedRef = useRef(false);

    // 评价业务参数：business_id 项目内为项目的business_id 独立智能体为智能体id ，resource_id 为智能体id
    const getEvaluateParams = () => {
        const { business_id, class_id, template_id } = getBusinessSearchParams();
        return {
            business_id: business_id || agentId,
            resource_id: agentId,
            template_id: template_id || 0,
            runtime_session_id: caseId,
            scene_id: sceneId,
            ...(class_id ? { class_id } : {})
        };
    };

    // 查询本人是否已评价（data.evl_flag），返回是否已评价
    const fetchEvaluationStatus = async () => {
        try {
            const res = await handleGetEvaluationStatus({ scene: CASE_SIMULATION_SCENE, ...getEvaluateParams() });
            const evlFlag = res?.code === '00000' && Boolean(res.data?.evl_flag);
            setEvaluated(evlFlag);
            return evlFlag;
        } catch (e) {
            console.error('查询评价状态失败', e);
            return false; // 接口异常按未评价放行，后端提交接口兜底拦截
        }
    };

    // 页面初始化：评价开关 + 已评价状态（只执行一次）
    const initEvaluate = async () => {
        if (evalInitedRef.current) return;
        evalInitedRef.current = true;
        const params = getEvaluateParams();
        try {
            const configRes = await handleGetBusinessConfig({ business_id: agentId, template_id: params.template_id });
            if (configRes?.code === '00000') {
                setEvaluateOpen(configRes.data?.evaluation_agent_open);
            }
        } catch (e) {
            console.error('查询评价开关失败', e);
        }
        fetchEvaluationStatus();
    };

    // 点击"去评价"：先查状态（防他端已评）→ 未评价打开评价弹窗
    const onEvaluateClick = async () => {
        const hasEvaluated = await fetchEvaluationStatus();
        if (hasEvaluated) {
            ToastUtils.showToast('您已经评价过了');
            return;
        }
        setEvaluateVisible(true);
    };

    // 评价提交成功：置已评价态并关闭弹窗
    const onEvaluateSuccess = () => {
        setEvaluated(true);
        setEvaluateVisible(false);
    };

    return {
        evaluateOpen, // 评价开关（1 开启）
        evaluated, // 是否已评价
        evaluateVisible, // 弹窗显隐
        setEvaluateVisible,
        getEvaluateParams, // 组装提交报文用的业务身份参数
        initEvaluate, // 页面挂载时调用一次
        onEvaluateClick, // 入口卡片点击
        onEvaluateSuccess // 提交成功回调
    };
};

export default useEvaluation;
